import { groupBy } from "lodash";
import { Config } from "services/configuration/eatSmartConfig";

import { AllergenType } from "types";
import { DishTypeCategory, LocalDishType, MealListSortTypeEnum } from "types/mainTypes";

/**
 * Filters the available dish types based on the given parameters.
 */
export const filterAvailableDishTypes = (
	fridgeDishTypes: LocalDishType[],
	availableDishTypes: Set<string>,
	allergens: AllergenType[],
	// Exclusive flag sets whether we filter meals, that do not include filtered allergens
	exclusive?: boolean,
): LocalDishType[] => {
	if (fridgeDishTypes) {
		return fridgeDishTypes.filter((dishType) => {
			const hasAllergen = allergens.some(
				(a: AllergenType) => dishType.allergens && dishType.allergens.includes(a),
			);

			return exclusive
				? availableDishTypes.has(dishType.id) && !hasAllergen
				: availableDishTypes.has(dishType.id) && hasAllergen;
		});
	}
	return [];
};
/**
 * Filters the available dish type IDs based on the given parameters.
 */
export const filterAvailableDishTypeIds = (
	fridgeDishTypes: LocalDishType[],
	availableDishTypes: Set<string>,
	allergens: AllergenType[],
): string[] => {
	const filteredDishTypes = filterAvailableDishTypes(
		fridgeDishTypes,
		availableDishTypes,
		allergens,
		true,
	);
	return filteredDishTypes.map((dishType) => dishType.id);
};

export const sortAndGroupDishTypesByCategory = (dishTypes: LocalDishType[]) => {
	return groupBy(
		dishTypes.sort((a, b) => {
			const categoryA = a.category ?? DishTypeCategory.MEAL;
			const categoryB = b.category ?? DishTypeCategory.MEAL;

			const orderA = Config.mealListOrder.categoryOrder[categoryA];
			const orderB = Config.mealListOrder.categoryOrder[categoryB];

			return orderA - orderB;
		}),
		"category",
	);
};

function sortDishTypesByName(
	a: LocalDishType,
	b: LocalDishType,
	language: "primary" | "secondary",
) {
	const descriptionA = a.description?.[language]?.name?.toLowerCase() ?? "";
	const descriptionB = b.description?.[language]?.name?.toLowerCase() ?? "";

	if (Config.mealListOrder.dishTypeOrderDirection === "DESC") {
		return descriptionA < descriptionB ? 1 : -1;
	}

	return descriptionA < descriptionB ? -1 : 1;
}

function sortDishTypesByPrice(a: LocalDishType, b: LocalDishType) {
	const priceA = parseInt(a.price?.current ?? "0");
	const priceB = parseInt(b.price?.current ?? "0");
	if (Config.mealListOrder.dishTypeOrderDirection === "DESC") {
		return priceB - priceA;
	}
	return priceA - priceB;
}

const safeParseNumber = (value: string | undefined | null): number => {
	const parsed = parseFloat(value ?? "");
	return Number.isFinite(parsed) ? parsed : 0;
};

export const getDiscountPercentage = (dishType: LocalDishType): number => {
	const current = safeParseNumber(dishType.price?.current);
	const defaultPrice = safeParseNumber(dishType.price?.default);
	if (defaultPrice <= 0 || current <= 0 || current >= defaultPrice) {
		return 0;
	}
	return Math.round((1 - current / defaultPrice) * 100);
};

export const isDishTypeDiscounted = (dishType: LocalDishType): boolean => {
	return getDiscountPercentage(dishType) > 0;
};

export const sortDishTypes = (
	dishTypes: LocalDishType[],
	selectedLanguage: "primary" | "secondary",
) => {
	const sortedDishTypesByCategory = sortAndGroupDishTypesByCategory(dishTypes);
	return Object.values(sortedDishTypesByCategory)
		.map((item) => {
			return item.sort((a: LocalDishType, b: LocalDishType) => {
				const aDiscount = getDiscountPercentage(a);
				const bDiscount = getDiscountPercentage(b);
				const aDiscounted = aDiscount > 0;
				const bDiscounted = bDiscount > 0;
				if (aDiscounted !== bDiscounted) return aDiscounted ? -1 : 1;
				if (aDiscounted && bDiscounted && aDiscount !== bDiscount) {
					return bDiscount - aDiscount;
				}

				switch (Config.mealListOrder.dishTypeOrder) {
					case MealListSortTypeEnum.NAME:
						return sortDishTypesByName(a, b, selectedLanguage);
					case MealListSortTypeEnum.PRICE: {
						return sortDishTypesByPrice(a, b);
					}
					default: {
						return a.id.localeCompare(b.id);
					}
				}
			});
		})
		.flat();
};
